import { Configuration, IPublicClientApplication } from "@azure/msal-browser";

export const TENANT_PORTAL_EXPLICIT_LOGOUT_KEY = 'tenantPortalExplicitLogout';

export const resetPasswordUri: string = process.env.REACT_APP_B2C_RPURI;

export const loginUri: string = process.env.REACT_APP_B2C_SUSIURI;

export const scopes: Array<string> = process.env.REACT_APP_B2C_SCOPES.split(',');

// Domain hint used to federate tenant users straight to Azure AD through B2C.
// Strip surrounding quotes so a pipeline-injected `""` falls back to the default.
export const b2cDomainHint: string =
    (process.env.REACT_APP_B2C_DOMAIN_HINT || '').replace(/^"+|"+$/g, '').trim() || 'commonaad';

// Optional allow-list of tenant portal origins (e.g. Icebar). Empty => path-only detection.
// The pipeline may inject a doubly-quoted empty value (e.g. `""`) when the release
// variable is set to `""` instead of blank, so strip surrounding quotes before parsing
// to treat it as truly empty.
export const tenantPortalOrigins: string[] = (
    process.env.REACT_APP_TENANT_PORTAL_ORIGINS || ''
)
    .replace(/^"+|"+$/g, '')
    .split(',')
    .map((o) => o.trim().replace(/^"+|"+$/g, ''))
    .filter(Boolean);

// Matches the URL shape produced by TCC_API GetClaimUrl(): /claims/{numericId}
const tenantDeeplinkPathPattern = /^\/claims\/\d+(\/|$)/;

/**
 * Detects whether the current top-level navigation is a tenant portal deeplink
 * (e.g. arriving from Icebar via /api/claims/web-portal-url). When true, auth
 * should be routed to Azure AD with domain_hint=commonaad.
 */
export function isTenantPortalEntry(): boolean {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get('tenant_entry') === '1') {
        return true;
    }

    if (!tenantDeeplinkPathPattern.test(globalThis.location.pathname)) {
        return false;
    }

    // No origins configured: path match alone is enough.
    if (tenantPortalOrigins.length === 0) {
        return true;
    }

    // Origins configured: also require navigation from a listed tenant portal.
    if (!document.referrer) {
        return false;
    }
    try {
        const referrerOrigin = new URL(document.referrer).origin;
        return tenantPortalOrigins.some((origin) => referrerOrigin === origin);
    } catch {
        return false;
    }
}

export function consumeTenantPortalExplicitLogout(): boolean {
    if (sessionStorage.getItem(TENANT_PORTAL_EXPLICIT_LOGOUT_KEY) === '1') {
        sessionStorage.removeItem(TENANT_PORTAL_EXPLICIT_LOGOUT_KEY);
        return true;
    }
    return false;
}

/**
 * Ends the MSAL session. Tenant portal deeplink users are sent to the app origin
 * so the direct-access login flow is shown instead of silent SSO re-login.
 */
export function performAppLogout(instance: IPublicClientApplication): void {
    if (isTenantPortalEntry()) {
        sessionStorage.setItem(TENANT_PORTAL_EXPLICIT_LOGOUT_KEY, '1');
        instance.logoutRedirect({
            account: instance.getActiveAccount() ?? undefined,
            postLogoutRedirectUri: globalThis.location.origin,
        });
        return;
    }

    instance.logout();
}

export const msalInstance: Configuration = {
    auth: {
        clientId: process.env.REACT_APP_B2C_CLIENTID,
        authority: loginUri,
        knownAuthorities: [process.env.REACT_APP_B2C_AUTHORITY],
        redirectUri: globalThis.location.origin,
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
    }
};